import React, { useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import {
  eligibleRequestedByUsers,
  nextRequestedById,
  requestedByApplies,
  requestedByLabel
} from '../../helpers/requestedBy';

export function useRequestedByField({ users, organizations, organizationId, isIndividual, enabled = true, setFormData }) {
  const organization = (organizations || []).find(item => item._id === organizationId) || null;
  const applies = enabled && requestedByApplies(organization, isIndividual);
  const eligible = useMemo(
    () => (applies ? eligibleRequestedByUsers(users, organization) : []),
    [applies, users, organization]
  );
  const eligibleKey = eligible.map(user => user._id).join('|');

  useEffect(() => {
    if (!enabled || !setFormData) return;
    setFormData(prev => {
      const currentOrganization = (organizations || []).find(item => item._id === prev.organization) || null;
      const currentApplies = requestedByApplies(currentOrganization, prev.isIndividualService);
      const currentEligible = currentApplies ? eligibleRequestedByUsers(users, currentOrganization) : [];
      const next = nextRequestedById({
        applies: currentApplies,
        eligible: currentEligible,
        requestedBy: prev.requestedBy || ''
      });
      if ((prev.requestedBy || '') === next) return prev;
      return { ...prev, requestedBy: next };
    });
  }, [enabled, organizationId, isIndividual, eligibleKey, users, organizations, setFormData]);

  return { applies, eligible };
}

const RequestedByField = ({ eligible, value, onChange, error }) => {
  const options = eligible.map(user => ({
    value: user._id,
    label: requestedByLabel(user)
  }));
  const selected = options.find(option => option.value === value) || null;
  const noUsers = eligible.length === 0;

  return (
    <Form.Group id="requestedBy">
      <Form.Label className="font-weight-bold">
        Requested By<span className="text-danger">*</span>
      </Form.Label>
      <Select
        inputId="requestedByInput"
        name="requestedBy"
        classNamePrefix="select"
        className={`select-organization ${error ? 'is-invalid' : ''}`}
        isSearchable
        isClearable={options.length > 1}
        isDisabled={noUsers}
        options={options}
        value={selected}
        onChange={option => onChange(option?.value || '')}
        placeholder={noUsers ? 'No enabled users' : 'Select...'}
        styles={
          error
            ? {
                control: (base, state) => ({
                  ...base,
                  borderColor: '#dc3545',
                  boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : 'none',
                  '&:hover': { borderColor: '#dc3545' }
                })
              }
            : undefined
        }
      />
      {error && <p className="xsmallFont text-rush mb-0">{error}</p>}
    </Form.Group>
  );
};

export default RequestedByField;
